import React, { memo, useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { useAppSelector } from 'store/hooks';
import { SCROLL_INDICATOR_INSETS, Spacer } from 'design-system';
import { IconBank, IconLightBulb } from 'design-system/icons';
import IconFolder from 'design-system/icons/IconFolder';
import { IconWifi } from 'design-system/icons/IconWifi';
import Section from 'design-system/Section';
import SetupCard from 'design-system/SetupCard';
import useEnergyCompare from 'features/feed/components/save/useEnergyCompare';
import { selectFeatureFlag } from 'reducers/selectors';
import { UnavailableOnWebModal } from 'utils/packages/UnavailableOnWebModal';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { isWeb } from '../../../constants';
const SaveOpportunitiesScreen = ({ scrollViewRef, onScroll, contentContainerStyle }) => {
    const navigation = useMainStackNavigation();
    const onCompare = useCallback((routeWithParams) => {
        navigation.navigateDeprecated(routeWithParams);
    }, []);
    const energyCompare = useEnergyCompare(onCompare);
    const hasEnergy = useAppSelector((store) => selectFeatureFlag(store, 'energy_switch_two').value);
    const hasCurrentAccountSwitchOffers = useAppSelector((store) => {
        const { value, extra } = selectFeatureFlag(store, 'current_account_switch_offers');
        return value && extra?.offers?.length;
    });
    const onPressCurrentAccounts = useCallback(() => navigation.navigateDeprecated('CurrentAccountSwitchOffers'), []);
    const onPressPensions = useCallback(() => navigation.navigateDeprecated('PensionBee'), []);
    const onPressBroadband = useCallback(() => energyCompare('Broadband'), [energyCompare]);
    const onPressEnergy = useCallback(() => {
        // The energy comparison runs in a WebView screen, which the web build doesn't have
        if (isWeb) {
            UnavailableOnWebModal.show('emma://save?tab=opportunities', 'View energy deals');
            return;
        }
        energyCompare('Energy');
    }, [energyCompare]);
    return (<View style={styles.container}>
      <Animated.ScrollView ref={scrollViewRef} onScroll={onScroll} style={styles.container} contentContainerStyle={contentContainerStyle} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} scrollEventThrottle={16}>
        <Spacer h={6}/>
        {!!hasCurrentAccountSwitchOffers && (<Section title="Switch current accounts">
            <SetupCard icon={<IconBank gradientKey="skyBlue"/>} title="Switch current accounts" subTitle="Upgrade your banking experience. Compare and switch current accounts for exclusive benefits, tailored features, and smarter ways to manage your money. Start your switch today!" buttonText="Browse offers" onButtonPress={onPressCurrentAccounts}/>
          </Section>)}
        <Section title="Combine pensions">
          <SetupCard icon={<IconFolder />} title="All your pensions in one place" subTitle="PensionBee combines your old pensions into one simple online plan. Use the app to track performance, adjust contributions, and make withdrawals when you reach retirement." buttonText="Combine pensions" onButtonPress={onPressPensions}/>
        </Section>
        <Section title="Save on broadband">
          <SetupCard icon={<IconWifi gradientKey="darkPurple"/>} title="Save on broadband" subTitle="Been with the same provider for a while? Customers get overcharged up to 40% more every year for staying too long with their provider." buttonText="View broadband deals" onButtonPress={onPressBroadband}/>
        </Section>

        {hasEnergy ? (<Section title="Save on energy">
            <SetupCard icon={<IconLightBulb gradientKey="orange"/>} title="Save on energy" subTitle="Switch energy for cost savings, green options, and better customer service. Enjoy flexibility, innovative tech, and avoid price increases. Make the switch today!" buttonText="View energy deals" onButtonPress={onPressEnergy}/>
          </Section>) : null}
      </Animated.ScrollView>
    </View>);
};
const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
});
export default memo(SaveOpportunitiesScreen);
